export const RESULT_KINDS = ["want_listing_draft", "inventory_draft"];

export const CONFIRM_ACTION_KINDS = ["publish", "syndicate", "delete", "archive"];

// A result's `kind` and an inline `[[card:...]]` marker's `refType` name the
// same two entities differently. Needed wherever code compares a result
// card against inline cards by id - refIds are only unique within a refType
// (see ListingCard.js), so an id match alone can collide inventory and
// want-listing rows that happen to share a number.
export const REF_TYPE_BY_RESULT_KIND = {
  want_listing_draft: "listing",
  inventory_draft: "inventory",
};

// The card's own disclosure that a draft is part machine-written. Leads with
// the count, because "some fields were assumed" is the kind of hedge an agent
// scrolls past, and a number is what makes them look. Neutral wording and
// neutral styling (see CopilotResultCard.scss): nothing here is wrong yet,
// the point is only that a person has not checked it.
export const DRAFTED_BY_COPILOT_LEAD = "Drafted by Genie.";

export const DEFAULT_CONFIRM_TEXT = "Are you sure? This cannot be undone.";

// Confirmation-dialog copy, per action kind - static and client-authored,
// never the model's. `action.confirm` (whatever the model sends) is a
// free-text field the model fully controls, and this dialog's entire job is
// to accurately describe a consequential action to the user before they
// commit to it - there is no backend endpoint yet for these actions to bind
// to (confirming just re-enters the chat/agent loop with a free-text
// prompt; see CopilotResultCard/index.js's runAction and the comment there
// - a typed per-action endpoint is a real follow-up, out of scope here), so
// this dialog is the only thing standing between the user and a
// consequential action, and it must not be something the model can spoof.
// Without this, the model could render a "delete" action whose confirm COPY
// says something harmless ("Saving your draft, nothing is removed") while
// its KIND is destructive (a gap caught in review). Keyed by kind, not by
// the individual action, so there's no per-action escape hatch either.
const CONFIRM_TEXT_BY_KIND = {
  delete: (title) => `This will permanently delete "${title}". This cannot be undone. Are you sure?`,
  archive: (title) => `This will archive "${title}". You can find it under archived items later. Are you sure?`,
  publish: (title) => `This will publish "${title}" so it becomes visible to others. Are you sure?`,
  syndicate: (title) => `This will syndicate "${title}" to external listing sites. Are you sure?`,
};

// The dialog body for a confirm action - always derived from the action's
// kind and the result's own title, never from action.confirm. A kind
// outside CONFIRM_TEXT_BY_KIND (there is currently no such kind - every
// entry in CONFIRM_ACTION_KINDS above has a mapping - but a future kind
// could be added to one list and not the other) falls back to a generic,
// still non-model prompt rather than ever reading action.confirm.
export const confirmTextForAction = (action, result) => {
  const kind = text(action?.kind);
  const title = result?.title || "this";
  const builder = kind && CONFIRM_TEXT_BY_KIND[kind];
  return builder ? builder(title) : DEFAULT_CONFIRM_TEXT;
};

const text = (value) =>
  typeof value === "string" && value.trim() ? value.trim() : null;

const normalizeField = (field) => {
  if (!field) return null;
  const key = text(field.key);
  const label = text(field.label);
  if (!key || !label) return null;
  return {
    key,
    label,
    value: field.value === undefined || field.value === null ? "" : field.value,
    unit: text(field.unit),
    type: text(field.type) || "text",
    assumed: field.assumed === true,
    editable: field.editable === true,
  };
};

export const isConfirmAction = (action) =>
  CONFIRM_ACTION_KINDS.includes(text(action?.kind));

// A bare, same-origin, app-relative path - nothing more. "Same-origin" is
// NOT a destination allowlist by itself: it would let a model reach any
// route in the app, including state-changing query params, so this checks
// three independent things and all three must hold.
//
// 1. The href must start with exactly one "/" (not "//"), checked FIRST and
//    unconditionally - this is the actual invariant ("app-relative path"),
//    and it must not depend on an origin comparison to hold. Two reasons:
//      - <Link to="evil"> and <Link to="../../x"> resolve against the
//        CURRENT ROUTE, not the app's origin - new URL(href, origin) turns
//        both into an in-origin path for the PURPOSES OF THIS CHECK, but
//        that's not where react-router-dom actually navigates, so an
//        origin-only check validates a target that diverges from the real
//        one.
//      - "https:evil.com" is scheme-relative: new URL() resolves it to
//        https://evil.com (same origin as any other absolute URL check
//        would expect it not to be - it fails on that alone), but
//        react-router-dom 6.15's <Link> matches it against its own
//        ABSOLUTE_URL_REGEX first and renders it as a raw, un-intercepted
//        external href before the origin check would ever run client-side.
//      - Requiring the leading "/" also closes a gap an origin comparison
//        can't: new URL("javascript:...").origin is the literal string
//        "null", and in an opaque origin (a sandboxed iframe without
//        allow-same-origin - this app ships an embed widget),
//        window.location.origin is ALSO "null", so an origin-only check
//        would let a javascript: URL through there. A leading-slash
//        requirement rejects "javascript:..." unconditionally, with no
//        dependency on what origin happens to be.
// 2. No "?" or "#" anywhere in the href - a bare path only. A query string
//    can carry a state-changing param (this app has ones that mutate on
//    load), so "same-origin" was never a strong enough bar for a
//    model-supplied destination; only a fixed, argument-free path is.
// 3. The origin check below, kept as defense in depth on top of (1) - it
//    still catches a bypass that survives the leading-slash check, e.g. a
//    leading backslash or an embedded tab/newline/CR that the URL parser's
//    own normalization turns into a second path separator (a bypass caught
//    in review).
// Exported so src/helpers/copilotInsights.js's own href (built from a
// different payload shape, but the same "app-relative path only" invariant)
// can reuse this exact check rather than drifting from it.
export const isSafeRelativeHref = (href) => {
  if (typeof href !== "string") return false;
  if (!href.startsWith("/") || href.startsWith("//")) return false;
  if (href.includes("?") || href.includes("#")) return false;
  if (typeof window === "undefined") return false;
  try {
    return new URL(href, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
};

const normalizeAction = (action) => {
  if (!action) return null;
  const kind = text(action.kind);
  const label = text(action.label);
  if (!kind || !label) return null;
  const href = text(action.href);
  if (isConfirmAction(action)) {
    // action.confirm (model-supplied) is intentionally NOT carried onto the
    // normalized action - see confirmTextForAction above. Nothing downstream
    // should ever have a way to read it and put it in front of the user.
    return {
      kind,
      label,
      href: null,
      primary: action.primary === true,
    };
  }
  if (!href || !isSafeRelativeHref(href)) return null;
  return { kind, label, href, primary: action.primary === true };
};

export const normalizeResult = (data) => {
  if (!data || !RESULT_KINDS.includes(data.kind)) return null;
  const id = data.id;
  if (typeof id !== "number" && !text(id)) return null;

  const fields = (Array.isArray(data.fields) ? data.fields : [])
    .map(normalizeField)
    .filter(Boolean);
  const actions = (Array.isArray(data.actions) ? data.actions : [])
    .map(normalizeAction)
    .filter(Boolean);
  const assumed = (Array.isArray(data.assumed) ? data.assumed : [])
    .map(text)
    .filter(Boolean);

  return {
    kind: data.kind,
    id,
    title: text(data.title) || "Draft",
    status: text(data.status),
    fields: fields.map((field) => ({
      ...field,
      assumed: field.assumed || assumed.includes(field.key),
    })),
    assumed,
    actions,
  };
};

export const applyFieldValue = (result, key, value) => {
  if (!result || !Array.isArray(result.fields)) return result;
  const index = result.fields.findIndex((field) => field.key === key);
  if (index === -1) return result;
  const fields = [...result.fields];
  fields[index] = { ...fields[index], value, assumed: false };
  return {
    ...result,
    fields,
    assumed: (result.assumed || []).filter((assumedKey) => assumedKey !== key),
  };
};

// Counted off the fields actually marked assumed, not off `result.assumed` -
// that array is a list of keys the model sent and can name a key the card has
// no field for (normalizeResult keeps it as-is for that reason), so counting
// it would claim more filled-in fields than the card shows.
export const assumedFieldCount = (result) =>
  (result?.fields || []).filter((field) => field.assumed).length;

export const assumedDisclosure = (result) => {
  const count = assumedFieldCount(result);
  if (count === 0) return null;
  const fields = count === 1 ? "1 field" : `${count} fields`;
  const them = count === 1 ? "it" : "them";
  return `Genie filled in ${fields} from the property and from what is typical nearby. Check ${them} before this goes live, and change anything marked Assumed right here.`;
};

export const formatFieldValue = (field) => {
  const value = field?.value;
  if (value === "" || value === null || value === undefined) return "Not set";
  return field.unit ? `${value} ${field.unit}` : `${value}`;
};

export const fieldInputType = (field) =>
  field?.type === "number" ? "number" : "text";
